import { CheckCircle, CrossCircleIcon, ReplaceIcon } from "assets/icons";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Icon } from "ui";

import {
  groupReturnVariantsByPack,
  ReturnPackEntry
} from "modules/returns/helpers/groupReturnVariantsByPack";
import {
  ReturnProductVariant,
  ReturnProductVariantSizeItem
} from "modules/returns/returns.types";
import { ReturnModalAtomProps } from "../../ReturnModal.types";
import { ReturnModalBlock } from "./ReturnModalBlock";

import styles from "../../ReturnModal.module.scss";

const ItemReviewBadges = ({
  item
}: {
  item: ReturnProductVariantSizeItem;
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.returnModal_productsBlock__review}>
      {!!item.quantityOfNotDefective && (
        <div
          className={classNames(
            styles.returnModal_productsBlock_reviewItem,
            styles.returnModal_productsBlock_reviewItem__noDefects
          )}
        >
          <Icon as={CheckCircle} width={18} height={18} color="#16c350" />
          {t("No defects")}: {item.quantityOfNotDefective}
        </div>
      )}

      {!!item.quantityReplaced && (
        <div
          className={classNames(
            styles.returnModal_productsBlock_reviewItem,
            styles.returnModal_productsBlock_reviewItem__replaced
          )}
        >
          <Icon as={ReplaceIcon} width={18} height={18} />
          {t("Replaced")}: {item.quantityReplaced}
        </div>
      )}

      {!!item.quantityThatCannotBeReplaced && (
        <div
          className={classNames(
            styles.returnModal_productsBlock_reviewItem,
            styles.returnModal_productsBlock_reviewItem__cantReplace
          )}
        >
          <Icon as={CrossCircleIcon} width={18} height={18} />
          {`${t("Can't be replaced")}: ${item.quantityThatCannotBeReplaced}`}
        </div>
      )}
    </div>
  );
};

const StandaloneVariantRow = ({
  productVariant,
  items
}: {
  productVariant: ReturnProductVariant;
  items: ReturnProductVariantSizeItem[];
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={styles.returnModal_productsBlock__product}
      key={productVariant.productVariantId}
    >
      <div className={styles.returnModal_productsBlock__imageThumb}>
        <img
          src={productVariant.image?.url}
          className={styles.returnModal_productsBlock__image}
        />
      </div>
      <div className={styles.returnModal_productsBlock__info}>
        <div className={styles.returnModal_productsBlock__title}>
          {productVariant.name}
        </div>

        {items.map((item) => (
          <div key={`productVariant-item-${item.barcodeId}`}>
            <div className={styles.returnModal_productsBlock__points}>
              <div className={styles.returnModal_productsBlock__point}>
                {t("Size")}: {item.sizeValue}
              </div>
              <span>·</span>
              <div className={styles.returnModal_productsBlock__point}>
                {t("Color")}: {productVariant.colorValue}
              </div>
              <span>·</span>
              <div className={styles.returnModal_productsBlock__point}>
                {t("Qty")}: {item.returnQuantity}
              </div>
            </div>

            <ItemReviewBadges item={item} />
          </div>
        ))}
      </div>
    </div>
  );
};

const PackCard = ({ entry }: { entry: ReturnPackEntry }) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        overflow: "hidden"
      }}
    >
      <div
        style={{
          padding: "10px 16px",
          backgroundColor: "#ECFDF5",
          fontWeight: 700,
          color: "#065F46",
          fontSize: 14
        }}
      >
        📦 {t("Pack")}
        {entry.packTitle ? ` · ${entry.packTitle}` : ""}
      </div>
      <div>
        {entry.items.map(({ productVariant, item }) => (
          <div
            className={styles.returnModal_productsBlock__product}
            key={`pack-${entry.packGroupId}-item-${item.barcodeId}`}
          >
            <div className={styles.returnModal_productsBlock__imageThumb}>
              <img
                src={productVariant.image?.url}
                className={styles.returnModal_productsBlock__image}
              />
            </div>
            <div className={styles.returnModal_productsBlock__info}>
              <div className={styles.returnModal_productsBlock__title}>
                {productVariant.name}
                {item.packSlotTitle ? ` — ${item.packSlotTitle}` : ""}
              </div>
              <div className={styles.returnModal_productsBlock__points}>
                <div className={styles.returnModal_productsBlock__point}>
                  {t("Size")}: {item.sizeValue}
                </div>
                <span>·</span>
                <div className={styles.returnModal_productsBlock__point}>
                  {t("Color")}: {productVariant.colorValue}
                </div>
                <span>·</span>
                <div className={styles.returnModal_productsBlock__point}>
                  {t("Qty")}: {item.returnQuantity}
                </div>
              </div>

              <ItemReviewBadges item={item} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ReturnModalItemsBlock = ({
  returnDetails
}: ReturnModalAtomProps) => {
  const { t } = useTranslation();
  const grouped = groupReturnVariantsByPack(returnDetails);

  return (
    <ReturnModalBlock label={t("Returning items")}>
      {grouped.map((entry) =>
        entry.kind === "pack" ? (
          <PackCard key={`pack-${entry.packGroupId}`} entry={entry} />
        ) : (
          <StandaloneVariantRow
            key={`standalone-${entry.productVariant.productVariantId}`}
            productVariant={entry.productVariant}
            items={entry.items}
          />
        )
      )}
    </ReturnModalBlock>
  );
};
